/**
 * V2 Account API Service
 * 일괄 조회 패턴으로 성능 개선
 */

import NetworkFetchError from '@/erros/NetworkFetchError'
import { apiInterceptor } from '@/networks/api/interceptor'
import type {
  IAccountV2,
  ICurrencyBalance,
  ITokenBalance,
} from '@/types/currency'

/**
 * Account 생성 파라미터
 */
export interface ICreateAccountParams {
  accounts: Array<{
    currencyId: string
    accountIndex: number
    label: string
    parentAccountId?: string
  }>
  UVD?: {
    b64SKey: string
    b64Data: string
  }
}

/**
 * Account 생성 응답 (단일 계정 정보)
 */
export interface IAccountInfo {
  id: string
  walletId: string
  currencyId: string
  addresses: string[]
  address?: string // V1 호환
  balance?: string
  label?: string
  accountIndex?: number
  accountClassName?: 'TypeAccount' | 'TokenAccount'
  used?: boolean
  lastSyncedAt?: string
  createdAt?: string
  updatedAt?: string
  parentAccountId?: string | null
  addressPath?: string
  pubkey?: string
  currency?: {
    name: string
    ticker: string
    symbol?: string
    decimals: number
    cgkId?: string
    iconUrl?: string
    contractAddress?: string
    color?: string
    networkSymbol?: string
  }
  accountData?: {
    addressPath?: string
    [key: string]: any
  }
}

export interface IAccountAPIServiceV2 {
  getAllAccounts(params?: {
    walletId?: string
    include?: string[]
  }): Promise<IAccountV2[]>
  getAccountById(walletId: string, accountId: string): Promise<IAccountV2>
  createAccount(
    walletId: string,
    params: ICreateAccountParams,
  ): Promise<IAccountInfo[]>
  updateAccount(
    walletId: string,
    accountId: string,
    updates: Partial<IAccountV2>,
  ): Promise<IAccountV2>
  deleteAccount(walletId: string, accountId: string): Promise<void>
  getAccountBalance(
    walletId: string,
    accountId: string,
  ): Promise<ICurrencyBalance>
  getMultipleAccountBalances(accountIds: string[]): Promise<ICurrencyBalance[]>
}

export class AccountAPIServiceV2 implements IAccountAPIServiceV2 {
  /**
   * 모든 계좌 일괄 조회 (V2)
   * V2: GET /wallet/{walletId}/account
   */
  async getAllAccounts(params?: {
    walletId?: string
    include?: string[]
  }): Promise<IAccountV2[]> {
    if (!params?.walletId) {
      throw new Error('walletId is required for getAllAccounts')
    }

    const queryParams = new URLSearchParams()

    if (params?.include?.length) {
      queryParams.append('include', params.include.join(','))
    }

    const queryString = queryParams.toString()
    const endpoint = queryString
      ? `/wallet/${params.walletId}/account?${queryString}`
      : `/wallet/${params.walletId}/account`

    const response = await apiInterceptor.request(endpoint)
    if (!response.ok) {
      throw new Error(`Failed to fetch accounts: ${response.statusText}`)
    }

    const data = await response.json()

    // V2 응답 구조: { success: true, data: [...], message: "..." }
    if (data.success && Array.isArray(data.data)) {
      return data.data
    }

    // Fallback
    return data.accounts || data
  }

  /**
   * 단일 계좌 조회 (V2)
   * V1: GET /account/:id
   * V2: GET /wallet/{walletId}/account/{accountId}?include=currency
   */
  async getAccountById(
    walletId: string,
    accountId: string,
  ): Promise<IAccountV2> {
    const response = await apiInterceptor.request(
      `/wallet/${walletId}/account/${accountId}?include=currency`,
    )
    if (!response.ok) {
      throw new Error(`Failed to fetch account: ${response.statusText}`)
    }

    const data = await response.json()

    // V2 응답 구조: { success: true, data: {...}, message: "..." }
    if (data.success && data.data) {
      return data.data
    }

    return data.account || data
  }

  /**
   * 계좌 생성 (V2)
   * V1: POST /account/create
   * V2: POST /wallet/{walletId}/account
   *
   * @param walletId - Wallet ID
   * @param params - accounts 배열 및 UVD 포함
   * @returns 생성된 계정 정보 배열
   */
  async createAccount(
    walletId: string,
    params: ICreateAccountParams,
  ): Promise<IAccountInfo[]> {
    const response = await apiInterceptor.request(
      `/wallet/${walletId}/account`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(params),
      },
    )

    if (!response.ok) {
      const errorData = await response.json()
      const errorInfo = errorData.error || {}
      const errorMessage =
        errorInfo.message || `Failed to create account: ${response.statusText}`

      // V2 에러를 V1 NetworkFetchError 형식으로 변환
      const apiResponse = {
        status: response.status,
        statusText: response.statusText,
        data: {
          message: {
            message: errorMessage,
            data: {
              RemainPinTryCnt: errorInfo.details?.remainPinTryCnt,
            },
          },
        },
      }

      throw new NetworkFetchError(apiResponse as any, errorMessage)
    }

    const data = await response.json()

    // V2 응답 구조 처리
    if (data.success && data.data) {
      return data.data // 배열로 반환
    }

    return data
  }

  /**
   * 계좌 업데이트 (V2)
   * V1: PATCH /account/{accountId}/modify
   * V2: PUT /wallet/{walletId}/account/{accountId}
   */
  async updateAccount(
    walletId: string,
    accountId: string,
    updates: Partial<IAccountV2>,
  ): Promise<IAccountV2> {
    const response = await apiInterceptor.request(
      `/wallet/${walletId}/account/${accountId}`,
      {
        method: 'PUT',
        body: JSON.stringify(updates),
      },
    )

    if (!response.ok) {
      throw new Error(`Failed to update account: ${response.statusText}`)
    }

    const data = await response.json()

    // V2 응답 구조 처리
    if (data.success && data.data) {
      return data.data
    }

    return data.account || data
  }

  /**
   * 계좌 삭제 (V2)
   * V1: DELETE /account/{accountId}
   * V2: DELETE /wallet/{walletId}/account/{accountId}
   */
  async deleteAccount(walletId: string, accountId: string): Promise<void> {
    const response = await apiInterceptor.request(
      `/wallet/${walletId}/account/${accountId}`,
      {
        method: 'DELETE',
      },
    )

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(
        errorData.error?.message ||
          `Failed to delete account: ${response.statusText}`,
      )
    }
  }

  /**
   * 단일 계좌 잔액 조회 (V2)
   * V1: GET /accountbalance/:id/balance
   * V2: GET /wallet/{walletId}/account/{accountId}/balance
   */
  async getAccountBalance(
    walletId: string,
    accountId: string,
  ): Promise<ICurrencyBalance> {
    const response = await apiInterceptor.request(
      `/wallet/${walletId}/account/${accountId}/balance`,
    )
    if (!response.ok) {
      throw new Error(`Failed to fetch account balance: ${response.statusText}`)
    }

    const data = await response.json()

    // V2 응답 구조 처리
    if (data.success && data.data) {
      return data.data
    }

    return data
  }

  /**
   * 여러 계좌 잔액 일괄 조회 (V2 최적화)
   * V1: 각 계좌마다 GET /accountbalance/:id/balance (M번 호출)
   * V2: POST /accounts/balances (1번 호출)
   */
  async getMultipleAccountBalances(
    accountIds: string[],
  ): Promise<ICurrencyBalance[]> {
    const response = await apiInterceptor.request('/accounts/balances', {
      method: 'POST',
      body: JSON.stringify({
        accountIds,
        includeTokens: true,
      }),
    })

    if (!response.ok) {
      throw new Error(
        `Failed to fetch account balances: ${response.statusText}`,
      )
    }

    const data = await response.json()
    return data.balances || data
  }

  /**
   * 계좌의 토큰 잔액 조회 (V2)
   * V1: GET /account-token?accountId=:id
   * V2: GET /account/:id/tokens
   */
  async getAccountTokens(accountId: string): Promise<ITokenBalance[]> {
    const response = await apiInterceptor.request(
      `/account/${accountId}/tokens`,
    )
    if (!response.ok) {
      throw new Error(`Failed to fetch account tokens: ${response.statusText}`)
    }

    const data = await response.json()
    return data.tokens || data
  }
}

// 싱글톤 인스턴스
export const accountAPIServiceV2 = new AccountAPIServiceV2()
